/**
 * Maps the analyzer's per-emotion scores to a sentiment valence so the page can
 * explain *what* is driving the dominant sentiment (e.g. "Negative — driven by
 * Anger 98%, traces of fear/sadness").
 *
 * Plutchik-ish grouping used by the analyzer:
 *   negative → anger, fear, sadness, disgust
 *   positive → joy, trust, anticipation
 *   surprise → swing/neutral; not attributed to either pole.
 */

export type Valence = 'negative' | 'positive' | 'neutral';

const VALENCE_OF: Record<string, Valence> = {
  anger: 'negative',
  fear: 'negative',
  sadness: 'negative',
  disgust: 'negative',
  joy: 'positive',
  trust: 'positive',
  anticipation: 'positive',
  // `surprise` is intentionally omitted — it doesn't load onto either pole.
};

export interface EmotionDriver {
  emotion: string;
  score: number;
  valence: Valence;
}

export interface SentimentDrivers {
  /** The pole the dominant emotion belongs to. */
  valence: Valence;
  /** Highest-scoring emotion that maps to a valence. */
  dominant: EmotionDriver;
  /** Same-pole supporting emotions with a non-trivial score, descending. */
  traces: EmotionDriver[];
}

const TRACE_THRESHOLD = 0.01; // 1% — below this is noise, not a "trace".

// Apollo stamps `__typename` onto every object; keep only real numeric scores.
function numericEmotionEntries(
  scores: Record<string, unknown>,
): EmotionDriver[] {
  return Object.entries(scores)
    .filter(
      ([k, v]) =>
        k !== '__typename' &&
        k in VALENCE_OF &&
        typeof v === 'number' &&
        Number.isFinite(v),
    )
    .map(([emotion, score]) => ({
      emotion,
      score: score as number,
      valence: VALENCE_OF[emotion],
    }));
}

/**
 * Derive the dominant driver and supporting traces from raw emotion scores.
 * Returns null when no valence-bearing emotion is present (graceful gating).
 */
export function deriveSentimentDrivers(
  scores: Record<string, unknown> | null | undefined,
): SentimentDrivers | null {
  if (!scores) return null;
  const drivers = numericEmotionEntries(scores).sort(
    (a, b) => b.score - a.score,
  );
  if (drivers.length === 0 || drivers[0].score <= 0) return null;

  const dominant = drivers[0];
  const traces = drivers
    .slice(1)
    .filter(
      (d) => d.valence === dominant.valence && d.score >= TRACE_THRESHOLD,
    );

  return { valence: dominant.valence, dominant, traces };
}

const VALENCE_LABEL: Record<Valence, string> = {
  negative: 'Negative',
  positive: 'Positive',
  neutral: 'Neutral',
};

export function valenceLabel(v: Valence): string {
  return VALENCE_LABEL[v];
}

export function capitalize(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}

export function pct(score: number): string {
  return `${Math.round(score * 100)}%`;
}
